import { StateCreator } from 'zustand';
import { IOrganizationsFields, IOrganizationsSlice } from './types';
import { organizationsApi, usersApi } from '@/API';
import { useInstitutionsStore } from '@/stores/institutions';
import {
  TOrganizationAdminInviteRequestCamel,
  TOrganizationRequestCamel,
  TOrganizationServerCamel,
} from '@/types/organizations';
import { TUserDataCamel } from '@/types/user';
import { convertKeys } from '@/utils/common';

const initState: IOrganizationsFields = {
  organizations: [],
  currentOrganization: null,
  organizationAdmins: [],
  organizationAdminsLoading: false,
};

/**
 * Builds the snake_case API request body shared by create and update
 * organization calls.
 * @param data - The create/update organization payload.
 * @returns The snake_case request payload.
 */
const buildOrganizationRequestBody = (data: TOrganizationRequestCamel) => {
  const body: TOrganizationRequestCamel = {
    name: data.name,
    language: data.language,
    country: data.country,
    state: data.state,
    address1: data.address1,
    address2: data.address2,
    city: data.city,
    zip: data.zip,
    pocSettings: {
      ...data.pocSettings,
      phone: data.pocSettings.phone?.number ? data.pocSettings.phone : null,
    },
    ...(data.configuration !== undefined && {
      configuration: data.configuration,
    }),
    ...(data.provisioning !== undefined && {
      provisioning: data.provisioning,
    }),
  };

  return convertKeys(body, 'snake');
};

export const organizationsSlice: StateCreator<IOrganizationsSlice> = (
  set,
  get,
) => ({
  ...initState,

  getOrganizations: async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      const result: TOrganizationServerCamel[] = convertKeys(
        response.data.data,
      );

      set({ organizations: result });

      return result;
    } catch (error) {
      console.error(error);
      set({ organizations: [], currentOrganization: null });
    }
    return [];
  },

  updateProvisioning: ({ seats, occupiedSeats, organizationId }) => {
    const withProvisioning = (
      organization: TOrganizationServerCamel,
    ): TOrganizationServerCamel => ({
      ...organization,
      provisioning: { ...organization.provisioning, seats, occupiedSeats },
    });

    set(({ organizations, currentOrganization }) => ({
      organizations: organizations.map((organization) =>
        organization.id === organizationId
          ? withProvisioning(organization)
          : organization,
      ),
      currentOrganization:
        currentOrganization && currentOrganization.id === organizationId
          ? withProvisioning(currentOrganization)
          : currentOrganization,
    }));
  },

  createOrganization: async (data: TOrganizationRequestCamel) => {
    try {
      const snakeBody = buildOrganizationRequestBody(data);
      const response = await organizationsApi.createOrganization(snakeBody);
      const organizationCamel: TOrganizationServerCamel = convertKeys(
        response.data.data,
      );

      set((state) => ({
        organizations: [...state.organizations, organizationCamel],
        currentOrganization: organizationCamel,
      }));
      useInstitutionsStore.getState().clearCurrentInstitution();
      return organizationCamel;
    } catch (error) {
      console.error('Create organization failed:', error);
      throw error;
    }
  },

  updateOrganization: async (id: string, data: TOrganizationRequestCamel) => {
    try {
      const snakeBody = buildOrganizationRequestBody(data);
      const response = await organizationsApi.updateOrganization(id, snakeBody);
      const organizationCamel: TOrganizationServerCamel = convertKeys(
        response.data.data,
      );

      set((state) => ({
        organizations: state.organizations.map((organization) =>
          organization.id === id ? organizationCamel : organization,
        ),
        currentOrganization:
          state.currentOrganization?.id === id
            ? organizationCamel
            : state.currentOrganization,
      }));
      return organizationCamel;
    } catch (error) {
      console.error('Update organization failed:', error);
      throw error;
    }
  },

  setCurrentOrganizationById: (id: string) => {
    const { organizations } = get();

    const organization = organizations.find((org) => org.id === id);

    if (!organization) {
      console.error(`Organization with ID "${id}" not found.`);
      return;
    }

    set({ currentOrganization: organization });
  },

  getOrganizationAdmins: async (organizationId: string) => {
    set({ organizationAdminsLoading: true });
    try {
      const response =
        await organizationsApi.getOrganizationAdmins(organizationId);
      const admins: TUserDataCamel[] = convertKeys(response.data.data);

      set({ organizationAdmins: admins });
    } catch (error) {
      console.error('Get organization admins failed:', error);
      set({ organizationAdmins: [] });
    } finally {
      set({ organizationAdminsLoading: false });
    }
  },

  inviteOrganizationAdmin: async (
    organizationId: string,
    data: TOrganizationAdminInviteRequestCamel,
  ) => {
    try {
      await organizationsApi.inviteOrganizationAdmin(
        organizationId,
        convertKeys(data, 'snake'),
      );
      await get().getOrganizationAdmins(organizationId);
    } catch (error) {
      console.error('Invite organization admin failed:', error);
      throw error;
    }
  },

  removeOrganizationAdmin: async (organizationId: string, userId: string) => {
    try {
      await organizationsApi.removeOrganizationAdmin(organizationId, userId);

      set((state) => ({
        organizationAdmins: state.organizationAdmins.filter(
          (admin) => admin.id !== userId,
        ),
      }));
    } catch (error) {
      console.error('Remove organization admin failed:', error);
      throw error;
    }
  },

  setOrganizationAdminStatus: async (userId: string, isActive: boolean) => {
    try {
      await usersApi.setActiveStatus(userId, isActive);

      set((state) => ({
        organizationAdmins: state.organizationAdmins.map((admin) =>
          admin.id === userId ? { ...admin, isActive } : admin,
        ),
      }));
    } catch (error) {
      console.error('Update organization admin status failed:', error);
      throw error;
    }
  },

  clearOrganizations: () => {
    set(initState);
  },
});
